import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { liveTaskQueryOptions } from "@/boot/query-sync";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { TextArea } from "@/components/ui/Field";
import { Status } from "@/components/ui/Status";
import { Markdown } from "@/lib/markdown";
import { DRAFT_NOTE, resultHeadline } from "@/lib/result";

interface TaskResult {
  id: string;
  task_version: number;
  state: "pending" | "confirmed" | "changes_requested" | "skipped";
  completion_note_md: string;
  method_id: string;
  memory: { title: string; summary: string; body_md: string; checks_md?: string; pitfalls_md?: string;
    existing_method_id?: string | null };
  confirmation: string | null;
  stale: boolean;
  /** Task f8e48cc9: false — an intermediate draft, nobody checks it yet. */
  final?: boolean;
}

export function TaskResultReview({ taskId, version, canReview }: {
  taskId: string; version: number; canReview: boolean;
}) {
  const qc = useQueryClient();
  const [correction, setCorrection] = useState("");
  const [fixing, setFixing] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const request = useRef<{ body: string; key: string } | null>(null);
  const result = useQuery({
    queryKey: ["task-result", taskId],
    queryFn: () => api<TaskResult | null>(`/api/v1/tasks/${taskId}/result`),
    ...liveTaskQueryOptions,
  });
  const submit = useMutation({
    mutationFn: (decision: "confirmed" | "changes_requested" | "skipped") => {
      const body = {
        version, result_id: result.data!.id, decision,
        confirmation: decision === "confirmed" ? "Проверил результат: работает"
          : decision === "skipped" ? skipReason.trim() : correction.trim(),
      };
      const serialized = JSON.stringify(body);
      if (request.current?.body !== serialized) {
        request.current = { body: serialized, key: crypto.randomUUID() };
      }
      return api<TaskResult>(`/api/v1/tasks/${taskId}/review-result`, {
        method: "POST", body, idempotent: request.current.key,
      });
    },
    onSuccess: () => {
      setCorrection("");
      setFixing(false);
      setSkipReason("");
      setSkipping(false);
      for (const key of ["task-result", "task", "tasks", "project-tasks", "methods",
        "method", "journal", "applications", "open-applications", "memory-stats"]) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });

  if (result.isError) return <Card>
    <p role="alert">Не удалось загрузить результат задачи. {result.error.message}</p>
    <Button onClick={() => void result.refetch()}>Повторить</Button>
  </Card>;
  const r = result.data;
  if (!r) return null;
  // The server decides: only an edit of the task's title or description makes a result
  // stale. A move, a link or a reopen raise the version too and must not lock «Подтвердить».
  const stale = r.stale;
  // Answered «поправить», nothing changed since: the person may still confirm it as it is.
  const reconsider = r.state === "changes_requested" && canReview && !stale;
  const { draft, mine, title } = resultHeadline(r, canReview);
  return <Card className={mine ? "space-y-3 border-2 border-p-needs" : "space-y-3"}>
    {/* Spec 020 D3: an agent's result is «Готово к проверке» until a person confirms it. */}
    {mine && <Status tone="needs" />}
    {r.state === "confirmed" && <Status tone="done" />}
    <h2 className="text-cell-title font-semibold">{title}</h2>
    {draft && <p className="text-meta text-p-text-muted">{DRAFT_NOTE}</p>}
    {/* PC audit 28.09, point 2: «Подтвердить» sat after the whole result — put it first. */}
    {reconsider && <div className="space-y-2 rounded-card bg-p-surface-2 p-3">
      <p className="text-body">Ты просил поправить{r.confirmation ? `: «${r.confirmation}»` : ""}. Если результат всё-таки в порядке — подтверди его как есть; иначе агент пришлёт новый.</p>
      <Button variant="primary" block loading={submit.isPending} disabled={submit.isPending}
        onClick={() => submit.mutate("confirmed")}>Подтвердить как есть</Button>
    </div>}
    <Markdown className="text-body" text={r.completion_note_md} />
    <div className="flex flex-wrap items-center gap-x-4 text-meta">
      <Link className="inline-flex min-h-touch items-center text-p-link" to={`/knowledge/${r.method_id}`}>
        Метод: {r.memory.title}
      </Link>
    </div>
    <details className="text-body">
      <summary className="cursor-pointer text-meta text-p-link">Что сохранено в метод</summary>
      <Markdown text={r.memory.body_md} />
      {r.memory.checks_md && <><p className="mt-3 font-semibold">Проверки</p><Markdown text={r.memory.checks_md} /></>}
      {r.memory.pitfalls_md && <><p className="mt-3 font-semibold">Ограничения</p><Markdown text={r.memory.pitfalls_md} /></>}
    </details>
    {r.state === "pending" && !draft && <>
      <p className="text-meta text-p-text-muted">{r.memory.existing_method_id
        ? "Подтвердишь — задача закроется, ответ сохранится в её истории."
        : "Подтвердишь — задача закроется, а метод получит доказательство успешного применения."}</p>
      {stale && <p role="alert" className="text-body">Задача или метод изменились после подготовки результата.
        Сначала «Поправить», чтобы агент сохранил актуальный результат.</p>}
      {canReview ? <>
        {/* The one yellow control of this screen (D2): the task screen has no bottom bar. */}
        <div className="grid grid-cols-2 gap-2">
          <Button variant="primary" loading={submit.isPending} disabled={stale || submit.isPending}
            onClick={() => submit.mutate("confirmed")}>Подтвердить</Button>
          <Button variant="secondary" disabled={submit.isPending} aria-expanded={fixing}
            onClick={() => setFixing(!fixing)}>Поправить</Button>
        </div>
        {fixing && <>
          <TextArea label="Что поправить в результате или методе" value={correction}
            onChange={(e) => setCorrection(e.target.value)} disabled={submit.isPending} rows={3} />
          <Button block variant="strong" disabled={!correction.trim() || submit.isPending}
            onClick={() => submit.mutate("changes_requested")}>Отправить правки</Button>
        </>}
        {!skipping ? (
          <button type="button" className="min-h-touch self-start text-body font-semibold text-p-link" disabled={submit.isPending}
            onClick={() => { setSkipping(true); setFixing(false); }}>Закрыть без проверки</button>
        ) : (
          <div className="space-y-2 rounded-card border-2 border-p-text p-3">
            <p className="text-cell-title font-semibold">Закрыть без проверки?</p>
            <p className="text-body">Задача закроется, но в списках будет «закрыта без проверки», а не «Сделано». Метод не получит доказательства.</p>
            <TextArea label="Почему — увидит исполнитель" value={skipReason}
              onChange={(e) => setSkipReason(e.target.value)} disabled={submit.isPending} rows={2} />
            <div className="grid grid-cols-2 gap-2">
              <Button variant="strong" loading={submit.isPending} disabled={!skipReason.trim() || submit.isPending}
                onClick={() => submit.mutate("skipped")}>Закрыть без проверки</Button>
              <Button variant="secondary" disabled={submit.isPending} onClick={() => setSkipping(false)}>Отмена</Button>
            </div>
          </div>
        )}
      </> : <p className="text-body">Проверку выполняет исполнитель задачи.</p>}
    </>}
    {r.confirmation && !reconsider && <p className="whitespace-pre-wrap text-body">Ответ: {r.confirmation}</p>}
    {submit.error && <div role="alert">
      <p>{submit.error.message}</p>
      <Button onClick={() => {
        void result.refetch();
        void qc.invalidateQueries({ queryKey: ["task", taskId] });
      }}>Обновить результат</Button>
    </div>}
  </Card>;
}
